import {
  AggregatableOperation,
  actSchema,
  formulaFunctions,
  orderedTimeDimensionFields,
  structuredFacetSchemas,
  systemFieldsData,
} from "./facets.generated.js";
import { downwardPaths } from "./facets.generated.js";
import { entityFacets } from "./facets.generated.js";
import { upwardPaths } from "./facets.generated.js";
import { setSchema } from "./facets.generated.js";
import { displayColumnInfo, metrics } from "./facets.generated.js";
import {
  EntityTypes,
  unitField,
  integrationsData,
} from "./facets.generated.js";
import { entityDefinitions } from "./facets.generated.js";
import { entityAttributeDefinitions } from "./facets.generated.js";
import { entityFacetDefinitions } from "./facets.generated.js";
const ACTIVITY_FACET = "Activity";
const NULL_STR = "__VZBL::null__";
const figureAttr = (f) =>
  f.attribute.indexOf(".") > -1 ? f.attribute.split(".").at(-1) : f.attribute;
export const isTimeDimension = (f) => {
  const attr = figureAttr(f);
  return attr in orderedTimeDimensionFields;
};
export const timeDimensionLabel = (f) => {
  const attr = figureAttr(f);
  if (attr in orderedTimeDimensionFields) {
    return orderedTimeDimensionFields[attr] || null;
  }
  return null;
};
const eventTimeColumn = {
  target: "Event",
  attribute: "occurredAt",
  type: "datetime",
  canonical: false,
  groupable: false,
  projected: false,
  machineOnly: false,
  many: false,
};
const enabledIntegrations = (config) => {
  const enabled = config.integrations || [];
  return enabled
    .map((c) => integrationsData.integrations.find((i) => i.name == c.name))
    .filter((i) => i);
};
const derivedEntities = (config) => {
  const derived = integrationsData.derived;
  const enabled = config.integrations || [];
  const allRoles = enabled.map((c) => c.roles).flat();
  const all = allRoles.map((role) => derived[role] || []).flat();
  return [...new Set(all)];
};
export const selectableEntities = (config) => {
  if (config) {
    const integrations = enabledIntegrations(config);
    const provided = integrations.map((i) => i?.provides || []).flat();
    const derived = derivedEntities(config);
    const all = [...provided, ...derived];
    return [...new Set(all)];
  } else {
    return [...EntityTypes];
  }
};
export const entityTypes = EntityTypes.reduce((res, e) => {
  res[e] = e;
  return res;
}, {});
const isMetric = (entity) => entity && metrics.includes(entity);
// given a search state (return entities list) - what are all the expected columns in scope?
const tableColumns = (raw, config) => {
  const state = raw;
  const isGlobalSearch = figureGlobalSearch(state);
  if (isGlobalSearch) {
    return GLOBAL_SEARCH_COLUMNS.map((c) => ({ target: "Item", attribute: c }));
  }
  const entities = [...(state.select || [])];
  if (entities.length == 0) {
    entities.push("Event");
  }
  const eventsInScope =
    state.action || state.actor || state.duration || isEvents(state);
  const allColumns = entities
    .map((e) => {
      let columns = entityColumns(e, config);
      if (eventsInScope && !isEvents(state)) {
        columns = [...columns];
      } else if (eventsInScope && isEvents(state)) {
        columns = [{ ...eventTimeColumn, target: e }, ...columns];
      }
      return columns
        .filter((c) => !c.machineOnly)
        .filter((c) => !c.projected)
        .map((f) => ({
          target: f.target,
          attribute: f.attribute,
          canonical: f.canonical,
        }));
    })
    .flat();
  // sort them -- time column first, then canonical, then anything else
  const scoreFor = (field) => {
    if (field.attribute == eventTimeColumn.attribute) return 0;
    if (field.canonical) return 1;
    return 2;
  };
  const sorted = allColumns.sort((c1, c2) => {
    let score = scoreFor(c1) - scoreFor(c2);
    if (score == 0) {
      score = c1.attribute.localeCompare(c2.attribute);
    }
    return score;
  });
  return unicize(sorted);
};
const GLOBAL_SEARCH_COLUMNS = [
  "lastActivityAt",
  "name",
  "EntityType",
  "integration",
];
const figureGlobalSearch = (state) =>
  (state.select || []).length == 0 && (state.search || "").trim().length > 0;
// default columns for display
// rn applies to viewing entities - not group bys
export const defaultDisplayColumns = (raw, config) => {
  const state = raw;
  if (state.groupBys && state.groupBys.length > 0) {
    return tableColumns(state, config);
  }
  const isGlobalSearch = figureGlobalSearch(state);
  const selected = selectedEntity(state);
  // base columns: default display columns, as indicated by the schema (+ special hardcoded case for global search)
  const baseColumns = isGlobalSearch
    ? [...GLOBAL_SEARCH_COLUMNS]
    : displayColumnInfo[selected] || [];
  // if the schema does not specify display columns, use the canonical field + 'integration'
  if (baseColumns.length == 0) {
    baseColumns.push(canonicalFieldFor(selected, config).attribute);
    baseColumns.push("integration");
  }
  const forDisplay = baseColumns.map((attribute) => ({
    target: selected,
    attribute,
  }));
  return forDisplay;
};
// aggregations and filters that the user specified and that should be displayed by default
export const userSpecifiedColumns = (raw) => {
  const state = raw;
  const selected = selectedEntity(state);
  const forDisplay = [];
  // we want to display aggregation fields
  const aggregations = (
    state.aggregations ? state.aggregations : [state.aggregation]
  ).filter((a) => a);
  forDisplay.push(...aggregations);
  // we also care about column values that are filtered.
  // we cannot count on them being part of the result set (and guaranteeing that would be iffy, bc it could be across a 1-n)
  // unless the column is on the selected entity
  const filters = state.filters || [];
  const isNotNullFiltersOnSelected = filters.filter((f) => {
    const notIn = f.comparison["NOT IN"] || [];
    return (
      notIn.length == 1 && notIn.includes(NULL_STR) && f.target == selected
    );
  });
  forDisplay.push(...isNotNullFiltersOnSelected);
  return forDisplay;
};
const reachableFromEntity = (entity, config) => {
  const relInfo = config.upwardRels || {};
  const candidates = relInfo[entity] || [];
  const allOptions = selectableEntities(config);
  return candidates.filter((c) => allOptions.includes(c));
};
const reachableDownwardFromEntity = (entity, config) => {
  const downwardRelInfo = config.downwardRels || {};
  const candidates = downwardRelInfo[entity] || [];
  const allOptions = selectableEntities(config);
  return candidates.filter((c) => allOptions.includes(c));
};
const entityColumns = (entity, config) => {
  const schemaInfo = config.schemaInfo || {};
  return schemaInfo[entity] || [];
};
const fieldInfo = (field, config) => {
  const fields = entityColumns(field.target, config);
  const fieldInfo = fields.find(
    (f) => f.attribute.toLowerCase() == field.attribute.toLowerCase(),
  );
  return fieldInfo;
};
// given a selected entity,
// figure the structured facets for that entity
// returns pseudo-attributes {target, attribute}[]
const facetFieldsFor = (selected, config) => {
  const facetSchema = structuredFacetSchemas[selected] || {};
  const facetFields = Object.entries(facetSchema)
    .map(([facetName, attrs]) => {
      if ("type" in attrs) {
        const columns = entityColumns(attrs.type, config)
          .filter((c) => !c.machineOnly)
          .map((c) => c.attribute);
        const candidates = attrs.attributes
          .filter((a) => columns.includes(a))
          .map((a) => ({ target: selected, attribute: `${facetName}.${a}` }));
        if (facetName == "Activity") {
          const activity = attrs.attributes
            .filter((a) => columns.includes(a))
            .map((a) => ({ target: facetName, attribute: a }));
          candidates.push(...activity);
        }
        return candidates;
      }
      return [];
    })
    .flat();
  return facetFields;
};
const selectedEntity = (state) =>
  (state.select || []).at(0) ||
  (figureGlobalSearch(state) && "Item") ||
  "Event";
/**
 * Determines all possible fields that can be used for grouping in a search query.
 * This includes:
 * 1. Fields from the selected entities
 * 2. Fields from reachable entities (entities that can be joined to)
 * 3. Facet fields
 * 4. Any explicitly specified groupBy fields
 *
 * @param raw - The raw search query state
 * @param config - The integration configuration containing schema and relationship information
 * @returns An array of Field objects that can be used for grouping
 *
 * @example
 * // Returns fields like:
 * [
 *   { target: "Event", attribute: "occurredAt", canonical: false },
 *   { target: "User", attribute: "name", canonical: true },
 *   { target: "Activity", attribute: "type" }
 * ]
 */
export const relevantGroupBys = (raw, config) => {
  const state = raw;
  const entities = [
    ...(state.select || []),
    ...(state.filters || []).map((f) => f.target),
    ...(state.groupBys || []).map((f) => f.target),
  ];
  const selected = selectedEntity(state);
  const facetFields = facetFieldsFor(selected, config);
  const uniqueEntities = [...new Set(entities)];
  const reachableEntities = uniqueEntities.flatMap((e) =>
    reachableFromEntity(e, config),
  );
  // duration / events / filters / groupBys
  const fields = [...reachableEntities, ...uniqueEntities].flatMap((e) => {
    const columns = entityColumns(e, config);
    return columns
      .filter((c) => !c.machineOnly && c.groupable)
      .map((c) => ({
        target: c.target,
        attribute: c.attribute,
        canonical: c.canonical,
      }));
  });
  return unicize([...(state.groupBys || []), ...fields, ...facetFields]);
};
const canonicalFieldFor = (entityOrEvent, config) => {
  return (
    entityColumns(entityOrEvent, config).find((c) => c.canonical) || {
      target: entityOrEvent,
      attribute: "name",
      canonical: true,
      groupable: false,
      projected: false,
      machineOnly: false,
      many: false,
    }
  );
};
const unicize = (fields) => {
  const seen = new Set();
  return fields.filter((f) => {
    const key = `${f.target}.${f.attribute}`;
    return seen.has(key) ? false : seen.add(key);
  });
};
// given a search state, what are the relevant filters
// this is defined as
// 0. downward reachable entities and fields
// 1. facets on the selected entities
// 2. and relevant groupBy fields
// 3. and Actor/Action on event-joining queries
export const relevantFilters = (raw, config) => {
  const state = raw;
  const entities = [...(state.select || [])];
  if (entities.length == 0) {
    const isGlobalSearch = figureGlobalSearch(state);
    if (isGlobalSearch) {
      entities.push("Item");
    } else {
      entities.push("Event"); // event by default
    }
  }
  const selected = entities[0];
  const facetFields = facetFieldsFor(selected, config);
  const uniqueEntities = [...new Set(entities)];
  const downwardReachableEntities = uniqueEntities
    .map((e) => reachableDownwardFromEntity(e, config))
    .flat();
  const upwardReachableEntities = uniqueEntities
    .map((e) => reachableFromEntity(e, config))
    .flat();
  let reachableEntities = [
    ...upwardReachableEntities,
    ...downwardReachableEntities,
  ];
  let reachableFields = reachableEntities
    .map((e) => {
      const columns = entityColumns(e, config);
      return columns
        .filter((a) => !a.machineOnly)
        .map((c) => ({
          target: c.target,
          attribute: c.attribute,
          canonical: c.canonical,
        }));
    })
    .flat();
  const fields = [
    ...tableColumns(raw, config),
    ...facetFields,
    ...reachableFields,
    ...relevantGroupBys(raw, config),
    ...(state.filters || []),
  ];
  return unicize(fields);
};
const isEvents = (state) => {
  const entities = [
    ...(state.select || []),
    ...(state.filters || []).map((f) => f.target),
    ...(state.groupBys || []).map((f) => f.target),
  ];
  return entities.every((e) => e === "Event");
};
// what is the column label for a given aggregation?
export const columnForAggregation = (aggregation) => {
  return aggregationKey(aggregation);
};
const aggregationKey = (aggregation) => {
  const fKey = (aggregation.filters || []).map(filterKey).join("_");
  return `${aggregation.reducer}(${aggregation.target}.${
    aggregation.attribute
  })${fKey.length > 0 ? `_f:${fKey}` : ""}`;
};
const fieldKey = (f) => `${f.target}.${f.attribute}`;
const filterKey = (f) => {
  const k = fieldKey(f);
  const [operator, values] = Object.entries(f.comparison).at(0) || [];
  return `${k}:${operator}:${values.sort().join(",")}`;
};
// given a search state, what are the relevant aggregations?
// -> the numeric fields of "the thing being grouped" +
// -> the datetime fields of "the thing being grouped" +
// -> the "things related to the thing being grouped"
// -> or the "things related to what is currently selected"
export const relevantAggregations = (raw, config) => {
  const state = raw;
  const entities = [...(state.select || [])];
  if (entities.length === 0) {
    const isGlobalSearch = figureGlobalSearch(state);
    if (isGlobalSearch) {
      entities.push("Item");
    } else {
      entities.push("Event"); // event by default
    }
  }
  const selected = entities[0];
  const facetFields = facetFieldsFor(selected, config);
  const uniqueEntities = [...new Set(entities)];
  const reachableEntities = uniqueEntities.flatMap((e) =>
    reachableDownwardFromEntity(e, config),
  );
  const groupBys = state.groupBys || [];
  if (groupBys.length > 0) {
    reachableEntities.push(...entities);
  }
  // we can aggregate
  // - by any 'many' super duper facets
  // - by any entity downwardly reachable from the currently selected ones (and its fields)
  const aggregatable = uniqueEntities
    .flatMap((e) => entityColumns(e, config))
    .filter((a) => a.many);
  const fields = reachableEntities.flatMap((e) => {
    const columns = entityColumns(e, config);
    const asFields = columns
      .filter((a) => a.type == "number" || a.type == "datetime")
      .filter((a) => !a.machineOnly)
      .map((c) => ({
        target: c.target,
        attribute: c.attribute,
        canonical: c.canonical,
      }));
    return [...asFields, { target: e, attribute: unitField, canonical: false }];
  });
  return unicize([...fields, ...aggregatable, ...facetFields]);
};
//TODO this is duplicated with chopin
const aggRegexp = /(\w+)\((.*)\)/;
const asField = (key) => {
  const match = key.match(aggRegexp);
  if (match) {
    const [_, reducer, fieldKey, ...rest] = match;
    const field = asField(fieldKey);
    return { ...field, reducer };
  }
  const [target, ...rest] = key.split(".");
  if (rest.length === 0) return { target: "Event", attribute: target };
  const attribute = rest.join(".");
  return { target, attribute };
};
const intervalRegexp = /.*intervals\(\w+\)/;
const unitTypeFor = (f, config) => {
  if ("reducer" in f) {
    const { unit, type } = unitTypeFor(
      {
        target: f.target,
        attribute: f.attribute,
      },
      config,
    );
    if ("count" == f.reducer) {
      return { type: "number" };
    } else if (
      ["sum", "max", "min", "avg", "p99", "p95", "p90", "p75", "p50"].includes(
        f.reducer,
      )
    ) {
      return { unit, type };
    }
  }
  // intervals facets
  if (f.attribute.match(intervalRegexp)) {
    // number interval
    return { type: "interval" };
  }
  if (f.attribute.split(".").length > 1) {
    const [attr, spec] = f.attribute.split(".");
    if (
      unitTypeFor({ target: f.target, attribute: attr }, config)?.type ==
      "datetime"
    ) {
      return { type: "interval" };
    }
  }
  if (f.attribute.split(".").length > 1) {
    const target = f.target;
    const [facetName, attribute] = f.attribute.split(".");
    const facetSpec = (structuredFacetSchemas[target] || {})[facetName];
    if (facetSpec == null) {
      console.log(`couldn't parse field ${JSON.stringify(f)} - null facetSpec`);
      return { type: "string" };
    }
    if ("type" in facetSpec) {
      const facetEntityType = facetSpec.type;
      return unitTypeFor({ target: facetEntityType, attribute }, config);
    } else {
      console.log(`couldn't parse field ${JSON.stringify(f)}`);
      return { type: "string" };
    }
  }
  // temporary cheat - bc we know the types are the same no matter the entity
  if (f.target == ACTIVITY_FACET) {
    return unitTypeFor(
      {
        target: "Event",
        attribute: `${f.target}.${f.attribute}`,
      },
      config,
    );
  }
  const { target, attribute } = f;
  const type = "string";
  let schemaType;
  let schemaUnit;
  if (target && attribute) {
    const fields = entityColumns(target, config);
    const field = fields.find(
      (f) => f.attribute.toLowerCase() == attribute.toLowerCase(),
    );
    schemaType = field?.type;
    schemaUnit = field?.unit;
    if (attribute == eventTimeColumn.attribute) {
      schemaType = "datetime";
    }
  }
  return { type: schemaType || type, unit: schemaUnit };
};
export const columnInfo = (colName, config) => {
  const field = asField(colName);
  return unitTypeFor(field, config);
};
// aka the output of the previous operations
export const relevantColumnsForFormula = (raw, config) => {
  const query = raw;
  // if (query.groupBys) {
  //   return query.aggregations
  // }
  const selected = selectedEntity(query);
  const fields = entityColumns(selected, config).filter(
    (f) => !f.machineOnly && !f.many,
  );
  // fields.
  const fieldInfo = fields.map((f) => ({
    col: f,
    type: unitTypeFor(f, config),
    key: fieldKey(f),
  }));
  const aggInfo = (query.aggregations || []).map((f) => ({
    col: f,
    type: unitTypeFor(f, config),
    key: aggregationKey(f),
  }));
  const ret =
    (query.groupBys?.length || 0) > 0 ? aggInfo : fieldInfo.concat(aggInfo);
  return ret.filter(
    ({ type }) => type.type == "number" || type.type == "datetime",
  );
};
// // aka those for formula + formulas
export const relevantColumnsForHaving = (raw, config) => {
  const query = raw;
  const info = relevantColumnsForFormula(query, config);
  const formulaTypes = (query.formulas || []).map((f) => ({
    key: f.name,
    type: figureFormulaUnitType(f.formula, info),
  }));
  return [...info, ...formulaTypes];
};
/**
 * exposes some context about entities and fields
 * which integration provides the entity
 * soon:
 *  - what relationship does this entity have with other entities
 *  - description
 *
 * what is the type (and unit) of the field
 * @param config
 * @param target
 * @param attribute
 */
export const humanContext = (config, target, attribute) => {
  const enabled = enabledIntegrations(config);
  const providedBy = enabled.find((i) => i.provides.includes(target));
  let fieldMeta;
  if (attribute) {
    fieldMeta = fieldInfo({ target, attribute }, config);
  }
  return {
    providedBy: providedBy?.name,
    fieldInfo: fieldMeta,
  };
};
export const referencedEntities = (state) => {
  // the selected entity
  const selected = (state.select || ["Event"])[0];
  const entities = [
    selected,
    ...[
      ...(state.filters || []),
      ...(state.groupBys || []),
      ...(state.aggregations || []),
      state.aggregation,
    ]
      .filter((f) => f != null)
      .map((f) => f.target),
  ]
    .filter((e) => e != null)
    .map((e) => (e == "Activity" ? "Event" : e)); //eek. facet resolution would be best
  return [...new Set(entities)];
};
const referencedFields = (config, state) => {
  // the selected entity
  const selected = (state.select || ["Event"])[0];
  const selectedFields = entityColumns(selected, config);
  // all fields that are examined / constrained
  const fields = [
    ...selectedFields,
    ...(state.filters || []),
    ...(state.groupBys || []),
    ...(state.aggregations || []),
    state.aggregation,
  ].filter((f) => f != null);
  return fields;
};
// issues a data structure that can be turned into a mermaid diagram
export const queryExplain = (config, state) => {
  // the selected entity
  const selected = (state.select || ["Event"])[0];
  // all fields that are examined / constrained
  const fields = referencedFields(config, state);
  const fieldEntities = fields.map((f) => f.target);
  const allEntities = [...new Set(fieldEntities)].filter((e) => e != selected);
  // Event
  const eventRels = [];
  const hasTimeConstraint = state.duration || state.startDate || state.endDate;
  if (selected != "Event" && hasTimeConstraint) {
    eventRels.push({
      from: "Event",
      to: selected,
      name: "Entity",
      dir: "direct",
    });
  }
  // Actor
  // facets take care of it?
  // facets
  const allDynamicFacetPaths = fields
    .map((f) => {
      const facets = entityFacets[f.target] || {};
      return facets[f.attribute];
    })
    .filter((f) => f)
    .flat();
  const allDynamicFacetRels = allDynamicFacetPaths.map(pathToRels).flat();
  // time constraints?
  // the entire graph
  // 1. get the relationships from pathProcessing, in a more detailed way than RelfInfo
  // figureEntityPaths is probably the right source
  // 2. feed it to the package
  // 3. return it here in schema form, once pruned for configured entities
  const relationships = [];
  relationships.push(...allDynamicFacetRels, ...eventRels);
  allEntities.forEach((e) => {
    // get paths from selected to e
    const up = getRels(upwardPaths, selected, e);
    const down = getRels(downwardPaths, selected, e);
    // add them to relationships
    relationships.push(...up);
    relationships.push(...down);
  });
  return {
    selected,
    entities: allEntities,
    relationships: [...new Set(relationships)],
    setSchema: setSchemaForConfig(config),
    actSchema: actSchemaForConfig(config),
  };
};
const getRels = (pathsInfo, from, to) => {
  const paths = (pathsInfo[from] || {})[to] || [];
  return paths.map(pathToRels).flat();
};
const pathToRels = (path) => {
  let from = path.start;
  return path.hops.map((hop) => {
    const r = { from, to: hop.tail, name: hop.rel, dir: hop.dir };
    from = hop.tail;
    return r;
  });
};
// placeholder
// given a config, what is the schema? (entities and their relationships)
// we approximate by returning the full schema,
// pruning the entities that are not provided
// 1. get the relationships from relSetSchema and relActSchema?
// yes
const setSchemaForConfig = (config) => {
  const dir = "direct";
  const entities = Object.keys(setSchema);
  const relationships = entities
    .map((from) =>
      Object.entries(setSchema[from]).map(([to, name]) => ({
        from,
        to,
        name,
        dir,
      })),
    )
    .flat();
  return {
    entities,
    relationships,
  };
};
const actSchemaForConfig = (config) => {
  const dir = "direct";
  const entities = Object.keys(actSchema);
  const relationships = entities
    .map((from) => {
      return Object.entries(actSchema[from])
        .map(([to, relationships]) => {
          const relNames = Object.values(relationships);
          return relNames.map((name) => ({ from, to, name, dir }));
        })
        .flat();
    })
    .flat();
  return {
    entities,
    relationships,
  };
};
//---------- aggregation
// functions for aggregation
export const aggregators = Object.keys(AggregatableOperation);
// the backend will happily compute any aggregate for the unit field
// but only 'sum' makes sense for the ui
export const aggregateFunctionsForField = (field, config) => {
  const allAggs = Object.keys(AggregatableOperation);
  if (
    field.target == null ||
    field.target == "" ||
    field.attribute == null ||
    field.attribute == ""
  ) {
    return allAggs;
  }
  if (field.attribute == unitField) return ["count"];
  const fieldKey = `${field.target}.${field.attribute}`;
  const { type } = columnInfo(fieldKey, config);
  if (type == "datetime") {
    return [
      AggregatableOperation.min,
      AggregatableOperation.max,
      AggregatableOperation.avg,
    ];
  } else if (type == "number") {
    return allAggs;
  } else if (type == "string") {
    return ["count"];
  } else if (type == "string[]") {
    return ["count"];
  }
  return allAggs;
};
export const isQuerySupported = (raw, config) => {
  const state = raw;
  const selected = (state.select || ["Event"])[0];
  // all fields that are examined / constrained
  const entities = [
    selected,
    ...(state.filters || []).map((f) => f.target),
    ...(state.groupBys || []).map((f) => f.target),
    ...(state.aggregations || []).map((f) => f.target),
    state.aggregation?.target,
  ].filter((f) => f != null);
  const queryEntities = [...new Set(entities)];
  const available = ["Activity", ...selectableEntities(config)];
  return queryEntities.every((e) => available.includes(e));
};
export const isSystemField = (attr) => systemFieldsData[attr] != null;
//------------formulas
export const hasFormulaReferences = (raw, f, config) => {
  const columns = relevantColumnsForFormula(raw, config);
  return isValid(f.formula, columns).result;
};
export const isValid = (formula, columns) => {
  const func = formula?.at(0);
  if (!func) {
    return { result: false, message: "" };
  }
  const funcSpec = formulaFunctions.find((f) => f.id == func);
  if (!funcSpec) {
    return { result: false, message: `function ${func} was not recognized` };
  }
  const paramTypeOptions = funcSpec.signature.map((s) =>
    s.input.map((i) => i.type),
  );
  const params = (formula?.slice(1) || []).filter((p) => p.length > 0);
  const validLength = paramTypeOptions.filter(
    (opts) => opts.length === params.length,
  );
  if (validLength.length === 0) {
    return {
      result: false,
      message: `${params.length} parameters given, expected ${paramTypeOptions
        .map((o) => o.length)
        .at(0)}`,
    };
  }
  //get either the column or, if a formula, the column, or if a constant, the constant
  const hydratedParams = params.map((p) =>
    hydrate(Array.isArray(p) ? p.at(0) : p, columns),
  );
  const paramsMatch = validLength.some((opts) => {
    return opts.every((o, i) => {
      const hydrated = hydratedParams[i];
      return hydrated !== undefined && matchesTypes(hydrated, [o]);
    });
  });
  if (!paramsMatch) {
    return {
      result: false,
      message: `the parameter types do not match the function signature(s)`,
    };
  }
  const recursivelyValid = params
    .filter((p) => Array.isArray(p))
    .every((p) => isValid(p, columns).result);
  return { result: recursivelyValid, message: "" };
};
// given the string key of a column of function, return the ColInfo or FormulaFunction
const hydrate = (item, columns) =>
  formulaFunctions.find((f) => f.id === item) ||
  columns.find((c) => c.key === item) ||
  (!Number.isNaN(Number(item)) ? { type: { type: "number" } } : undefined);
export const matchesTypes = (item, possibleTypes) => {
  if (possibleTypes === undefined) return true;
  return "signature" in item
    ? item.signature
        .map((s) => s.return)
        .some((t) => possibleTypes.includes(t.type))
    : possibleTypes.includes(item.type.type);
};
export const formulaToString = (f, columns, inner = false) => {
  if (f === undefined) return "";
  if (typeof f === "string") {
    return f;
  }
  const func = f.at(0);
  const funcSpec = hydrate(func, columns);
  if (funcSpec === undefined) {
    return "";
  } else if ("col" in funcSpec) {
    // shouldn't happen
    return "";
  } else {
    const params = f
      .slice(1)
      .map((p, i) =>
        formulaToString(
          (p?.length || 0) > 0 ? p : `'param ${i + 1}'`,
          columns,
          true,
        ),
      );
    const isOperator = "operator" in funcSpec && funcSpec.operator;
    const ret = isOperator
      ? `${params.at(0)} ${func} ${params.at(1)}`
      : `${func}(${params.join(",")})`;
    return inner && isOperator ? `(${ret})` : ret;
  }
};
formulaFunctions.forEach((f) => {
  f.signature.forEach((s) => {
    if ("serializedReturnUnit" in s) {
      s["returnUnit"] = new Function(`return ${s.serializedReturnUnit}`)();
    }
  });
});
const figureFormulaUnitType = (formula, columns) => {
  const [funcId, ...params] = formula;
  const inUse = formulaFunctions.find((f) => f.id == funcId);
  if (inUse != null) {
    const paramTypes = params
      .map((p) => columns.find((c) => c.key == p)?.type)
      .filter((t) => t);
    // figure matching signature
    const signatures = inUse.signature;
    const signature = signatures.find((s) => {
      s.input.every((t, i) => paramTypes.at(i)?.type == t.type);
    });
    if (signature == undefined) {
      return { type: "number" };
    }
    // get return unit
    const unit = (signature || {}).returnUnit(...paramTypes);
    const type = signature?.return.type;
    return { type, unit };
  }
  return { type: "number" };
};
// figure possible signatures, given (partial) params and columns
export const figureFormulaParams = (func, params, columns) => {
  let paramLengths = 0;
  let paramTypeOptions;
  const inUse = formulaFunctions.find((f) => f.id == func);
  if (inUse != null) {
    const possibleParamLengths = inUse.signature.map((s) => s.input.length);
    // pretend there is only ever one.
    paramLengths = possibleParamLengths.at(0) || 0;
    // now figure which signature(s) are the "most valid" given what we have selected so far
    const inputSignatureTypes = inUse.signature.map((s) =>
      s.input.map((i) => i.type),
    );
    const paramTypesOptionsWithScore = inputSignatureTypes.map((opts) => {
      let highestMatchingIndex = 0;
      while (true) {
        const option = opts[highestMatchingIndex];
        const param = params[highestMatchingIndex];
        const colOrFunc = Array.isArray(param) ? param.at(0) : param;
        const item = hydrate(colOrFunc, columns);
        const match = item !== undefined && matchesTypes(item, [option]);
        if (match) {
          highestMatchingIndex += 1;
        } else {
          break;
        }
      }
      return [highestMatchingIndex, opts];
    });
    const scores = paramTypesOptionsWithScore.map((p) => p[0]);
    const highScore = Math.max(...scores);
    paramTypeOptions = paramTypesOptionsWithScore
      .filter(([score, _opts]) => score === highScore)
      .map(([_score, opts]) => opts);
  }
  return { paramTypeOptions, paramLengths };
};
export const entityDefinition = (name) => entityDefinitions[name];
// TODO Activity etc
export const entityAttributeDefinition = (name) => {
  const attr = figureEntityAttributeDefinition(
    name,
    entityAttributeDefinitions,
  );
  if (attr) return attr;
  return figureEntityAttributeDefinition(name, entityFacetDefinitions);
};
const isIntervals = (name) =>
  name.startsWith("intervals(") && name.endsWith(")");
const intervalField = (name) => name.replace("intervals(", "").replace(")", "");
/**
 * handle Activity.blah
 * Issue.Activity
 * Issue.Activity.blah
 * Issue.intervals(vzblCycleTime)
 *
 * Issue.createdAt.Day
 * @param name
 * @param data
 * @returns
 */
const figureEntityAttributeDefinition = (name, data) => {
  // Issue.Attribute
  const parts = name.split(".");
  const entity = parts[0];
  const isActivity = parts[1] == "Activity";
  const attrs = isActivity ? data["Activity"] : data[entity];
  if (attrs) {
    const rest = isActivity
      ? parts.slice(2).join(".")
      : parts.slice(1).join(".");
    const interval = isIntervals(rest);
    const attribute = interval ? intervalField(rest) : rest;
    if (rest == "" && isActivity) return "Events on the current entity";
    const attrSpec = (attrs.attributes || attrs.facets).find(
      (a) => a.name == attribute,
    );
    if (attrSpec) {
      return attrSpec.description;
    }
    // not found, trying for Issue.createdAt.Day
    if (rest.includes(".")) {
      const parts = rest.split(".");
      const attrSpec = (attrs.attributes || attrs.facets).find(
        (a) => a.name == parts[0],
      );
      if (attrSpec && attrSpec.kind == "dateFields") {
        const asActivityName = `Activity.${parts[1]}`;
        const asActivityDefinition =
          figureEntityAttributeDefinition(
            asActivityName,
            entityFacetDefinitions,
          ) || "";
        return `${attrSpec.description} -- ${asActivityDefinition}`;
      }
    }
  }
};
